import type {
  AnnouncementStep,
  NightActionPayload,
  OneNightAction,
  RoleDefinition,
  RoleId,
  RoleTeam,
} from "@cloudnest/one-night-ultimate-werewolf-engine";
import {
  DEFAULT_DISCUSSION_DURATION_SECONDS,
  ROLE_DEFINITIONS,
} from "@cloudnest/one-night-ultimate-werewolf-engine";
import { useEffect, useMemo, useRef, useState } from "react";
import { navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { getParticipantDisplayNames } from "../../shared/utils/participants";
import { OneNightEventLog } from "./components/OneNightEventLog";
import {
  type CenterCardInteraction,
  OneNightRosterBoard,
  phaseModeOf,
  type RosterTileInteraction,
} from "./components/OneNightRosterBoard";
import { deriveLogEntries, type OneNightLogEntry } from "./event-log";
import {
  gameActions,
  type OneNightViewerState,
  useGameActions,
  useGameStore,
} from "./multiplayer-game-store";
import "./styles/one-night-ultimate-werewolf-client.css";

const phaseModeChips = new Map<string, string>([
  ["setup", "⚙ 준비"],
  ["night", "🌙 밤"],
  ["day", "☀️ 낮"],
  ["vote", "🗳️ 투표"],
  ["result", "🏁 결과"],
]);

import {
  outcomeReasonLabels,
  phaseLabels,
  roleLabels,
  teamLabels,
} from "./labels";

const roleOrder: readonly RoleId[] = [
  "werewolf",
  "seer",
  "robber",
  "troublemaker",
  "villager",
  "hunter",
  "tanner",
  "minion",
  "mason",
  "doppelganger",
  "drunk",
  "insomniac",
];

const emptyRoleIds: readonly RoleId[] = [];
const centerSlotIndexes = [0, 1, 2] as const;
const discussionDurationOptions = [180, 300, 420, 600] as const;

type CenterSlotIndex = (typeof centerSlotIndexes)[number];
type SeerTargetMode = "center" | "player";

function isRevealedCard(
  viewerState: OneNightViewerState | null,
  cardId: string | null,
): boolean {
  if (!cardId) return false;
  return viewerState?.visibleCards[cardId]?.visibility === "revealed";
}

function formatCountdown(seconds: number | null): string {
  if (seconds === null) return "대기";
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  return `${minutes}:${remainder.toString().padStart(2, "0")}`;
}

function countRole(roleIds: readonly RoleId[], roleId: RoleId): number {
  return roleIds.filter((candidate) => candidate === roleId).length;
}

function countTeam(roleIds: readonly RoleId[], team: RoleTeam): number {
  return roleIds.filter((roleId) => ROLE_DEFINITIONS[roleId].team === team)
    .length;
}

function isRoleSelectionReady(
  roleIds: readonly RoleId[],
  targetCount: number,
): boolean {
  if (roleIds.length !== targetCount) return false;
  return roleOrder.every(
    (roleId) =>
      countRole(roleIds, roleId) <= ROLE_DEFINITIONS[roleId].maxCopies,
  );
}

function makeBeginnerPreset(targetCount: number): readonly RoleId[] {
  const preset: RoleId[] = [
    "werewolf",
    "werewolf",
    "seer",
    "robber",
    "troublemaker",
    "villager",
  ];
  const fallbackOrder: readonly RoleId[] = [
    "villager",
    "villager",
    "hunter",
    "tanner",
    "minion",
    "mason",
    "mason",
    "doppelganger",
    "drunk",
    "insomniac",
  ];

  for (const roleId of fallbackOrder) {
    if (preset.length >= targetCount) break;
    if (roleId === "mason") {
      // Masons are only legal as a pair — skip unless both copies fit.
      if (
        countRole(preset, "mason") === 0 &&
        preset.length + 2 <= targetCount
      ) {
        preset.push("mason", "mason");
      }
      continue;
    }
    if (countRole(preset, roleId) < ROLE_DEFINITIONS[roleId].maxCopies) {
      preset.push(roleId);
    }
  }

  return preset.slice(0, targetCount);
}

function roleSelectionKey(roleIds: readonly RoleId[]): string {
  return roleIds.join("|");
}

function roleDefinition(roleId: RoleId): RoleDefinition {
  return ROLE_DEFINITIONS[roleId];
}

function currentPendingAnnouncement(
  announcerTimeline: readonly AnnouncementStep[],
): AnnouncementStep | null {
  return announcerTimeline.find((step) => step.status === "pending") ?? null;
}

function isAnnouncementComplete(
  announcerTimeline: readonly AnnouncementStep[],
  roleId: RoleId,
): boolean {
  return announcerTimeline.some(
    (step) => step.roleId === roleId && step.status === "complete",
  );
}

function nightScript(roleId: RoleId): string {
  switch (roleId) {
    case "werewolf":
      return "늑대인간은 눈을 뜨고 서로를 확인하십시오.";
    case "minion":
      return "미니언은 눈을 뜨고 늑대인간을 확인하십시오.";
    case "mason":
      return "메이슨은 눈을 뜨고 서로를 확인하십시오.";
    case "doppelganger":
      return "도플갱어는 눈을 뜨고 다른 플레이어 카드 1장을 확인해 그 역할이 되십시오.";
    case "seer":
      return "예언자는 눈을 뜨고 확인할 카드를 선택하십시오.";
    case "robber":
      return "강도는 눈을 뜨고 교환할 플레이어를 선택하십시오.";
    case "troublemaker":
      return "트러블메이커는 눈을 뜨고 서로 바꿀 두 플레이어를 선택하십시오.";
    case "drunk":
      return "주정뱅이는 눈을 뜨고 중앙 카드 1장을 선택하십시오.";
    case "insomniac":
      return "불면증 환자는 눈을 뜨고 현재 자기 역할을 확인하십시오.";
    default:
      return `${roleLabels.get(roleId) ?? "역할"} 차례입니다.`;
  }
}

function isExplicitTargetRole(roleId: RoleId): boolean {
  return (
    roleId === "seer" ||
    roleId === "robber" ||
    roleId === "troublemaker" ||
    roleId === "drunk"
  );
}

function selectedNightActionPayload(
  roleId: RoleId,
  seerTargetMode: SeerTargetMode,
  selectedPlayerTargetIds: readonly string[],
  selectedCenterTargetIndexes: readonly CenterSlotIndex[],
  canInspectCenter: boolean,
): NightActionPayload | null {
  switch (roleId) {
    case "werewolf": {
      if (!canInspectCenter) return { kind: "confirmNoAction" };
      const [centerSlotIndex] = selectedCenterTargetIndexes;
      return centerSlotIndex !== undefined
        ? { kind: "werewolfPeekCenter", centerSlotIndex }
        : null;
    }
    case "seer": {
      if (seerTargetMode === "player") {
        const targetPlayerId = selectedPlayerTargetIds[0];
        return targetPlayerId
          ? { kind: "seerInspectPlayer", targetPlayerId }
          : null;
      }
      const [left, right] = selectedCenterTargetIndexes;
      return left !== undefined && right !== undefined
        ? { kind: "seerInspectCenter", centerSlotIndexes: [left, right] }
        : null;
    }
    case "robber": {
      const targetPlayerId = selectedPlayerTargetIds[0];
      return targetPlayerId ? { kind: "robberSwap", targetPlayerId } : null;
    }
    case "troublemaker": {
      const [left, right] = selectedPlayerTargetIds;
      return left && right
        ? { kind: "troublemakerSwap", targetPlayerIds: [left, right] }
        : null;
    }
    case "drunk": {
      const [centerSlotIndex] = selectedCenterTargetIndexes;
      return centerSlotIndex !== undefined
        ? { kind: "drunkSwapCenter", centerSlotIndex }
        : null;
    }
    case "insomniac":
      return { kind: "insomniacInspectOwn" };
    case "minion":
      return { kind: "minionSeeWerewolves" };
    case "mason":
      return { kind: "masonSeeMasons" };
    default:
      return { kind: "confirmNoAction" };
  }
}

function nightActionButtonLabel(
  roleId: RoleId,
  canInspectCenter: boolean,
): string {
  switch (roleId) {
    case "werewolf":
      return canInspectCenter ? "중앙 카드 확인" : "행동 완료";
    case "seer":
      return "선택 확인";
    case "robber":
      return "교환 확인";
    case "troublemaker":
      return "교환 확인";
    case "drunk":
      return "중앙 카드 교환";
    case "insomniac":
      return "내 카드 확인";
    case "minion":
      return "늑대 확인 완료";
    case "mason":
      return "메이슨 확인 완료";
    default:
      return "행동 완료";
  }
}

function nightSelectionHint(
  roleId: RoleId,
  seerTargetMode: SeerTargetMode,
  selectedPlayerTargetIds: readonly string[],
  selectedCenterTargetIndexes: readonly CenterSlotIndex[],
  canInspectCenter: boolean,
): string {
  switch (roleId) {
    case "werewolf":
      return canInspectCenter
        ? `중앙 카드 ${selectedCenterTargetIndexes.length}/1`
        : "서로의 늑대인간을 확인";
    case "seer":
      return seerTargetMode === "center"
        ? `중앙 카드 ${selectedCenterTargetIndexes.length}/2`
        : `플레이어 ${selectedPlayerTargetIds.length}/1`;
    case "robber":
      return `교환 대상 ${selectedPlayerTargetIds.length}/1`;
    case "troublemaker":
      return `교환 대상 ${selectedPlayerTargetIds.length}/2`;
    case "drunk":
      return `중앙 카드 ${selectedCenterTargetIndexes.length}/1`;
    case "insomniac":
      return "현재 자기 카드 확인";
    case "minion":
      return "늑대인간 위치 확인 (늑대는 미니언을 모름)";
    case "mason":
      return "다른 메이슨 상호 확인";
    default:
      return "대상 선택 없음";
  }
}

export function GameScreen() {
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { gameState, viewerState, error } = useGameStore();
  const actions = useGameActions();
  const roomId = currentRoom?.id ?? null;
  const userId = user?.id ?? null;
  const isHost = currentRoom?.hostUserId === userId;
  const [draftRoleIds, setDraftRoleIds] = useState<readonly RoleId[]>([]);
  const [draftDiscussionDurationSeconds, setDraftDiscussionDurationSeconds] =
    useState<number>(DEFAULT_DISCUSSION_DURATION_SECONDS);
  const [seerTargetMode, setSeerTargetMode] =
    useState<SeerTargetMode>("center");
  const [selectedPlayerTargetIds, setSelectedPlayerTargetIds] = useState<
    readonly string[]
  >([]);
  const [selectedCenterTargetIndexes, setSelectedCenterTargetIndexes] =
    useState<readonly CenterSlotIndex[]>([]);
  const [selectedVoteTargetId, setSelectedVoteTargetId] = useState<
    string | null
  >(null);
  const [logEntries, setLogEntries] = useState<readonly OneNightLogEntry[]>([]);
  const [logExpanded, setLogExpanded] = useState(false);
  const previousLogViewRef = useRef<OneNightViewerState | null>(null);

  useEffect(() => {
    if (!viewerState) {
      previousLogViewRef.current = null;
      setLogEntries([]);
      return;
    }
    const previous = previousLogViewRef.current;
    if (previous === viewerState) return;
    previousLogViewRef.current = viewerState;
    const appended = deriveLogEntries(previous, viewerState);
    if (appended.length > 0) {
      setLogEntries((current) => [...current, ...appended]);
    }
  }, [viewerState]);

  const actionsRef = useRef(actions);
  const lobbyActionsRef = useRef(lobbyActions);
  useEffect(() => {
    actionsRef.current = actions;
    lobbyActionsRef.current = lobbyActions;
  }, [actions, lobbyActions]);

  const { connectionStatus } = useGameEventsWS(roomId, {
    onGameEvent: () => {},
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void gameActions.replayEvents(roomId);
    },
    onViewerState: (viewerState, seq) => {
      actionsRef.current.applyRemoteViewerState(viewerState, seq);
    },
  });

  const playerIds = currentRoom?.participantUserIds ?? [];
  const playerNames = getParticipantDisplayNames(currentRoom, "플레이어");
  const persistedRoleIds = gameState?.selectedRoleIds ?? emptyRoleIds;

  // biome-ignore lint/correctness/useExhaustiveDependencies: use primitive room/player keys so WS room updates do not reinitialize the store.
  useEffect(() => {
    if (!roomId || !currentRoom || !user || playerIds.length < 3) return;

    if (isHost) {
      void gameActions.initializeAsHost(
        roomId,
        user.id,
        playerIds,
        playerNames,
      );
      return;
    }

    void gameActions.initializeAsGuest(roomId, user.id);
  }, [
    roomId,
    currentRoom?.id,
    user?.id,
    isHost,
    playerIds.join("|"),
    playerNames.join("|"),
  ]);

  useEffect(() => {
    setDraftRoleIds(persistedRoleIds);
  }, [persistedRoleIds]);

  useEffect(() => {
    if (gameState?.discussionDurationSeconds === undefined) return;
    setDraftDiscussionDurationSeconds(gameState.discussionDurationSeconds);
  }, [gameState?.discussionDurationSeconds]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset transient target picks when entering a new night role (or the doppelganger's copied phase).
  useEffect(() => {
    setSeerTargetMode("center");
    setSelectedPlayerTargetIds([]);
    setSelectedCenterTargetIndexes([]);
  }, [
    gameState?.phase,
    viewerState?.viewerNightAction?.roleId,
    viewerState?.viewerNightAction?.copiedRoleId,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: clear transient vote picks when the phase or viewer changes.
  useEffect(() => {
    setSelectedVoteTargetId(null);
  }, [gameState?.phase, user?.id]);

  const playerById = useMemo(
    () =>
      new Map(
        (gameState?.players ?? []).map(
          (player) => [player.id, player] as const,
        ),
      ),
    [gameState?.players],
  );

  const targetRoleCount = gameState ? gameState.players.length + 3 : 0;
  const draftRoleCount = draftRoleIds.length;
  const draftSelectionReady = isRoleSelectionReady(
    draftRoleIds,
    targetRoleCount,
  );
  const persistedSelectionReady = gameState
    ? isRoleSelectionReady(gameState.selectedRoleIds, targetRoleCount)
    : false;
  const rolesChanged =
    !!gameState &&
    roleSelectionKey(draftRoleIds) !==
      roleSelectionKey(gameState.selectedRoleIds);
  const discussionDurationChanged =
    !!gameState &&
    draftDiscussionDurationSeconds !== gameState.discussionDurationSeconds;
  const myPlayer = user ? playerById.get(user.id) : undefined;
  const myCurrentCardId =
    viewerState?.viewerCurrentCardId ?? myPlayer?.startingCardId ?? null;
  const myRoleId = myCurrentCardId
    ? (viewerState?.visibleCards[myCurrentCardId]?.roleId ?? null)
    : null;
  const rolePeekConfirmed =
    !!user && !!gameState?.rolePeekConfirmedPlayerIds.includes(user.id);
  const rolePeekConfirmedCount =
    gameState?.rolePeekConfirmedPlayerIds.length ?? 0;
  const allRolePeekConfirmed =
    !!gameState && rolePeekConfirmedCount === gameState.players.length;
  const pendingAnnouncement = gameState
    ? currentPendingAnnouncement(gameState.announcerTimeline)
    : null;
  const viewerNightAction = viewerState?.viewerNightAction ?? null;
  const canInspectCenterAsWerewolf =
    viewerNightAction?.roleId === "werewolf" &&
    viewerNightAction.canInspectCenter === true;
  // Doppelganger two-phase step: copy a role first, then (for immediate-action
  // roles) perform the copied role's action inside the same step.
  const doppelgangerCopiedRoleId =
    viewerNightAction?.roleId === "doppelganger"
      ? (viewerNightAction.copiedRoleId ?? null)
      : null;
  const isDoppelgangerCopyPhase =
    viewerNightAction?.roleId === "doppelganger" &&
    doppelgangerCopiedRoleId === null;
  const effectiveNightRoleId: RoleId | null = viewerNightAction
    ? viewerNightAction.roleId === "doppelganger" && doppelgangerCopiedRoleId
      ? doppelgangerCopiedRoleId
      : viewerNightAction.roleId
    : null;
  const nightActionAnnouncementReady =
    !!gameState &&
    !!viewerNightAction &&
    isAnnouncementComplete(
      gameState.announcerTimeline,
      viewerNightAction.roleId,
    );
  const nightActionPayload: NightActionPayload | null =
    viewerNightAction?.status === "required" && effectiveNightRoleId
      ? isDoppelgangerCopyPhase
        ? selectedPlayerTargetIds[0]
          ? {
              kind: "doppelgangerCopy",
              targetPlayerId: selectedPlayerTargetIds[0],
            }
          : null
        : selectedNightActionPayload(
            effectiveNightRoleId,
            seerTargetMode,
            selectedPlayerTargetIds,
            selectedCenterTargetIndexes,
            canInspectCenterAsWerewolf,
          )
      : null;
  const selectableNightPlayers = user
    ? (gameState?.players ?? []).filter((player) => player.id !== user.id)
    : [];
  const nightSelectionSummary = viewerNightAction
    ? isDoppelgangerCopyPhase
      ? `복사 대상 ${selectedPlayerTargetIds.length}/1`
      : nightSelectionHint(
          effectiveNightRoleId ?? viewerNightAction.roleId,
          seerTargetMode,
          selectedPlayerTargetIds,
          selectedCenterTargetIndexes,
          canInspectCenterAsWerewolf,
        )
    : "";
  const voteLockedPlayerIds = gameState?.voteLockedPlayerIds ?? [];
  const voteLockedPlayerIdSet = useMemo(
    () => new Set(voteLockedPlayerIds),
    [voteLockedPlayerIds],
  );
  const voteLockedCount = voteLockedPlayerIds.length;
  const allVotesLocked =
    !!gameState &&
    gameState.players.length > 0 &&
    voteLockedCount === gameState.players.length;
  const myVoteLocked = !!user && voteLockedPlayerIdSet.has(user.id);
  const discussionRemainingSeconds =
    gameState?.discussionRemainingSeconds ?? null;
  const voteRemainingSeconds = gameState?.voteRemainingSeconds ?? null;
  const voteTimedOut = voteRemainingSeconds === 0;
  const canResolveVote = allVotesLocked || voteTimedOut;
  const selectableVotePlayers = user
    ? (gameState?.players ?? []).filter((player) => player.id !== user.id)
    : [];
  const voteCountsByPlayerId = useMemo(
    () =>
      new Map(
        (gameState?.voteResult?.voteCounts ?? []).map((voteCount) => [
          voteCount.playerId,
          voteCount,
        ]),
      ),
    [gameState?.voteResult],
  );
  const revealedCardLocations = viewerState?.revealedCardLocations ?? [];
  const revealedPlayerCardIdByPlayerId = useMemo(
    () =>
      new Map(
        revealedCardLocations.flatMap((location) =>
          location.kind === "player"
            ? ([[location.playerId, location.cardId]] as const)
            : [],
        ),
      ),
    [revealedCardLocations],
  );
  const revealedCenterCardIdBySlotIndex = useMemo(
    () =>
      new Map(
        revealedCardLocations.flatMap((location) =>
          location.kind === "center"
            ? ([[location.slotIndex, location.cardId]] as const)
            : [],
        ),
      ),
    [revealedCardLocations],
  );

  const submitGameAction = async (action: OneNightAction) => {
    await actions.submitAction(action);
  };

  useEffect(() => {
    if (
      !userId ||
      !isHost ||
      gameState?.phase !== "day" ||
      discussionRemainingSeconds === null ||
      discussionRemainingSeconds <= 0
    ) {
      return;
    }

    const timerId = window.setTimeout(() => {
      void actions.submitAction({
        type: "tickDiscussion",
        actorPlayerId: userId,
        elapsedSeconds: 1,
      });
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [actions, discussionRemainingSeconds, gameState?.phase, isHost, userId]);

  useEffect(() => {
    if (!userId || !isHost || gameState?.phase !== "vote") {
      return;
    }

    if (canResolveVote) {
      const timerId = window.setTimeout(() => {
        void actions.submitAction({
          type: "resolveVote",
          actorPlayerId: userId,
        });
      }, 250);
      return () => window.clearTimeout(timerId);
    }

    if (voteRemainingSeconds === null || voteRemainingSeconds <= 0) {
      return;
    }

    const timerId = window.setTimeout(() => {
      void actions.submitAction({
        type: "tickVote",
        actorPlayerId: userId,
        elapsedSeconds: 1,
      });
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [
    actions,
    canResolveVote,
    gameState?.phase,
    isHost,
    userId,
    voteRemainingSeconds,
  ]);

  const moveSeat = (index: number, direction: -1 | 1) => {
    if (!gameState || !user || gameState.seatLocked) return;
    const targetIndex = index + direction;
    if (
      index <= 0 ||
      targetIndex <= 0 ||
      targetIndex >= gameState.seatOrder.length
    ) {
      return;
    }

    const nextSeatOrder = gameState.seatOrder.map((playerId, currentIndex) => {
      if (currentIndex === index) {
        return gameState.seatOrder[targetIndex] ?? playerId;
      }
      if (currentIndex === targetIndex) {
        return gameState.seatOrder[index] ?? playerId;
      }
      return playerId;
    });

    void submitGameAction({
      type: "updateSeatOrder",
      actorPlayerId: user.id,
      seatOrder: nextSeatOrder,
    });
  };

  const addDraftRole = (roleId: RoleId) => {
    const definition = roleDefinition(roleId);
    if (
      draftRoleIds.length >= targetRoleCount ||
      countRole(draftRoleIds, roleId) >= definition.maxCopies
    ) {
      return;
    }
    setDraftRoleIds([...draftRoleIds, roleId]);
  };

  const removeDraftRole = (roleId: RoleId) => {
    const removalIndex = draftRoleIds.lastIndexOf(roleId);
    if (removalIndex < 0) return;
    setDraftRoleIds(draftRoleIds.filter((_, index) => index !== removalIndex));
  };

  const saveRoles = () => {
    if (!user || !draftSelectionReady) return;
    void submitGameAction({
      type: "selectRoles",
      actorPlayerId: user.id,
      roleIds: draftRoleIds,
    });
  };

  const saveDiscussionDuration = () => {
    if (!user || !discussionDurationChanged) return;
    void submitGameAction({
      type: "setDiscussionDuration",
      actorPlayerId: user.id,
      durationSeconds: draftDiscussionDurationSeconds,
    });
  };

  const lockSeatOrder = () => {
    if (!user || !gameState || gameState.seatLocked) return;
    void submitGameAction({
      type: "lockSeatOrder",
      actorPlayerId: user.id,
    });
  };

  const dealCards = () => {
    if (!user || !gameState?.seatLocked || !persistedSelectionReady) {
      return;
    }
    void submitGameAction({
      type: "dealCards",
      actorPlayerId: user.id,
    });
  };

  const confirmRolePeek = () => {
    if (!user || !gameState || rolePeekConfirmed) return;
    void submitGameAction({
      type: "confirmRolePeek",
      actorPlayerId: user.id,
    });
  };

  const startNight = () => {
    if (!user || !gameState || !isHost || !allRolePeekConfirmed) return;
    void submitGameAction({
      type: "startNight",
      actorPlayerId: user.id,
    });
  };

  const advanceAnnouncement = () => {
    if (!user || !pendingAnnouncement || !isHost) return;
    void submitGameAction({
      type: "advanceAnnouncement",
      actorPlayerId: user.id,
      roleId: pendingAnnouncement.roleId,
    });
  };

  const chooseSeerTargetMode = (targetMode: SeerTargetMode) => {
    setSeerTargetMode(targetMode);
    setSelectedPlayerTargetIds([]);
    setSelectedCenterTargetIndexes([]);
  };

  const toggleCenterTarget = (slotIndex: CenterSlotIndex) => {
    setSelectedCenterTargetIndexes((current) => {
      if (current.includes(slotIndex)) {
        return current.filter((candidate) => candidate !== slotIndex);
      }
      if (current.length >= 2) {
        const [, right] = current;
        return right === undefined ? [slotIndex] : [right, slotIndex];
      }
      return [...current, slotIndex];
    });
  };

  const selectSinglePlayerTarget = (playerId: string) => {
    setSelectedPlayerTargetIds([playerId]);
  };

  const toggleTroublemakerTarget = (playerId: string) => {
    setSelectedPlayerTargetIds((current) => {
      if (current.includes(playerId)) {
        return current.filter((candidate) => candidate !== playerId);
      }
      if (current.length >= 2) {
        const [, right] = current;
        return right === undefined ? [playerId] : [right, playerId];
      }
      return [...current, playerId];
    });
  };

  const submitCurrentNightAction = () => {
    if (
      !user ||
      !viewerNightAction ||
      viewerNightAction.status !== "required" ||
      !nightActionAnnouncementReady ||
      !nightActionPayload
    ) {
      return;
    }
    void submitGameAction({
      type: "submitNightAction",
      actorPlayerId: user.id,
      roleId: viewerNightAction.roleId,
      payload: nightActionPayload,
    });
  };

  const lockVote = () => {
    if (!user || !selectedVoteTargetId || myVoteLocked) return;
    void submitGameAction({
      type: "castVote",
      actorPlayerId: user.id,
      targetPlayerId: selectedVoteTargetId,
    });
  };

  const startVote = () => {
    if (
      !user ||
      !isHost ||
      gameState?.phase !== "day" ||
      discussionRemainingSeconds !== 0
    ) {
      return;
    }
    void submitGameAction({
      type: "startVote",
      actorPlayerId: user.id,
    });
  };

  const resolveVote = () => {
    if (!user || !isHost || !canResolveVote || gameState?.phase !== "vote") {
      return;
    }
    void submitGameAction({
      type: "resolveVote",
      actorPlayerId: user.id,
    });
  };

  const revealPlayerCard = (playerId: string) => {
    if (!user || !isHost) return;
    void submitGameAction({
      type: "revealCard",
      actorPlayerId: user.id,
      target: { kind: "player", playerId },
    });
  };

  const revealCenterCard = (slotIndex: CenterSlotIndex) => {
    if (!user || !isHost) return;
    void submitGameAction({
      type: "revealCard",
      actorPlayerId: user.id,
      target: { kind: "center", slotIndex },
    });
  };

  const completeRound = () => {
    if (!user || !isHost || gameState?.phase !== "reveal") return;
    void submitGameAction({
      type: "completeRound",
      actorPlayerId: user.id,
    });
  };

  const startNextRound = () => {
    if (!user || !isHost || gameState?.phase !== "complete") return;
    void submitGameAction({
      type: "startNextRound",
      actorPlayerId: user.id,
    });
  };

  const phaseMode = gameState ? phaseModeOf(gameState.phase) : "setup";
  const nightSelectionActive =
    gameState?.phase === "night" &&
    viewerNightAction?.status === "required" &&
    nightActionAnnouncementReady;

  const tileInteraction: RosterTileInteraction | undefined = (() => {
    if (!user || !gameState) return undefined;
    if (gameState.phase === "vote" && !myVoteLocked) {
      return {
        selectablePlayerIds: selectableVotePlayers.map((player) => player.id),
        selectedPlayerIds: selectedVoteTargetId ? [selectedVoteTargetId] : [],
        onSelectPlayer: (playerId: string) => setSelectedVoteTargetId(playerId),
      };
    }
    if (nightSelectionActive && viewerNightAction && effectiveNightRoleId) {
      const roleId = effectiveNightRoleId;
      if (
        isDoppelgangerCopyPhase ||
        roleId === "robber" ||
        (roleId === "seer" && seerTargetMode === "player")
      ) {
        return {
          selectablePlayerIds: selectableNightPlayers.map(
            (player) => player.id,
          ),
          selectedPlayerIds: selectedPlayerTargetIds,
          onSelectPlayer: selectSinglePlayerTarget,
        };
      }
      if (roleId === "troublemaker") {
        return {
          selectablePlayerIds: selectableNightPlayers.map(
            (player) => player.id,
          ),
          selectedPlayerIds: selectedPlayerTargetIds,
          onSelectPlayer: toggleTroublemakerTarget,
        };
      }
    }
    return undefined;
  })();

  const centerInteraction: CenterCardInteraction | undefined = (() => {
    if (
      !nightSelectionActive ||
      !viewerNightAction ||
      !effectiveNightRoleId ||
      isDoppelgangerCopyPhase
    ) {
      return undefined;
    }
    const roleId = effectiveNightRoleId;
    if (roleId === "werewolf" && canInspectCenterAsWerewolf) {
      return {
        selectableSlots: [...centerSlotIndexes],
        selectedSlots: selectedCenterTargetIndexes,
        onSelectSlot: (slotIndex: CenterSlotIndex) =>
          setSelectedCenterTargetIndexes([slotIndex]),
      };
    }
    if (roleId === "seer" && seerTargetMode === "center") {
      return {
        selectableSlots: [...centerSlotIndexes],
        selectedSlots: selectedCenterTargetIndexes,
        onSelectSlot: toggleCenterTarget,
      };
    }
    if (roleId === "drunk") {
      return {
        selectableSlots: [...centerSlotIndexes],
        selectedSlots: selectedCenterTargetIndexes,
        onSelectSlot: (slotIndex: CenterSlotIndex) =>
          setSelectedCenterTargetIndexes([slotIndex]),
      };
    }
    return undefined;
  })();

  const renderVotePanel = () => {
    if (!gameState) return null;
    if (gameState.phase === "day") {
      const discussionTimeLabel = formatCountdown(
        discussionRemainingSeconds ?? gameState.discussionDurationSeconds,
      );
      const voteWindowLabel = formatCountdown(gameState.voteWindowSeconds);

      return (
        <section
          className="onuw-client-vote-panel"
          data-testid="onuw-vote-panel"
        >
          <div className="onuw-client-panel-heading">
            <span>낮 토론 · 타이머 진행</span>
            <strong>토론이 끝나면 투표창이 자동으로 열립니다</strong>
          </div>
          <div className="onuw-client-timer-strip">
            <span>남은 토론</span>
            <strong data-testid="onuw-discussion-timer">
              {discussionTimeLabel}
            </strong>
          </div>
          <p>
            토론 중에는 투표 대상을 고르지 않습니다. 카운터가 0이 되면 모든
            플레이어에게 {voteWindowLabel} 투표창이 표시됩니다.
          </p>
          <div className="onuw-client-flow-summary">
            <span>{isHost ? "방장 자동 진행" : "방장 타이머 대기"}</span>
            <span>미투표 시 본인 자동 투표</span>
          </div>
          {isHost && discussionRemainingSeconds === 0 ? (
            <div className="onuw-client-phase-actions">
              <button
                data-testid="onuw-start-vote"
                onClick={startVote}
                type="button"
              >
                투표창 열기
              </button>
            </div>
          ) : null}
        </section>
      );
    }

    const voteTimeLabel = formatCountdown(
      voteRemainingSeconds ?? gameState.voteWindowSeconds,
    );
    const statusCopy = myVoteLocked
      ? "투표를 완료했습니다. 모든 플레이어가 완료하면 결과가 바로 표시됩니다."
      : "10초 안에 한 명을 선택해 투표합니다. 시간이 지나면 내게 자동 투표됩니다.";

    return (
      <section className="onuw-client-vote-panel" data-testid="onuw-vote-panel">
        <div className="onuw-client-panel-heading">
          <span>10초 투표창</span>
          <strong data-testid="onuw-vote-progress">
            투표 {voteLockedCount}/{gameState.players.length}
          </strong>
        </div>
        <div className="onuw-client-timer-strip is-vote">
          <span>남은 투표</span>
          <strong data-testid="onuw-vote-timer">{voteTimeLabel}</strong>
        </div>
        <p>{statusCopy}</p>
        <div className="onuw-client-flow-summary">
          <span>{allVotesLocked ? "모든 투표 완료" : "투표 진행 중"}</span>
          <span data-testid="onuw-auto-vote-note">
            {voteTimedOut ? "미투표 자동 처리" : "미투표 시 본인"}
          </span>
        </div>
        <p data-testid="onuw-vote-tile-hint">
          위 보드에서 투표할 플레이어 타일을 직접 탭하세요.
          {selectedVoteTargetId
            ? ` 선택됨: ${playerById.get(selectedVoteTargetId)?.name ?? ""}`
            : " 아직 선택하지 않았습니다."}
        </p>
        <div className="onuw-client-phase-actions">
          <button
            data-testid="onuw-lock-vote"
            disabled={!selectedVoteTargetId || myVoteLocked}
            onClick={lockVote}
            type="button"
          >
            {myVoteLocked ? "투표 완료" : "투표 확정"}
          </button>
          {isHost && gameState.phase === "vote" ? (
            <button
              data-testid="onuw-resolve-vote"
              disabled={!canResolveVote}
              onClick={resolveVote}
              type="button"
            >
              투표 결과 표시
            </button>
          ) : null}
        </div>
      </section>
    );
  };

  const renderRevealPanel = () => {
    if (!gameState?.voteResult) return null;
    const outcome = gameState.gameOutcome;
    const outcomeLabel = outcome
      ? (outcomeReasonLabels.get(outcome.reason) ?? outcome.reason)
      : "승패 판정 대기";
    const winningTeams = !outcome
      ? "대기"
      : outcome.winningTeams.length === 0
        ? "없음 (시민편 패배)"
        : outcome.winningTeams
            .map((team) => teamLabels.get(team) ?? team)
            .join(" · ");
    const autoVoteNames = gameState.autoVotePlayerIds.map(
      (playerId) => playerById.get(playerId)?.name ?? "플레이어",
    );
    const isComplete = gameState.phase === "complete";

    return (
      <section
        className={
          isComplete
            ? "is-complete onuw-client-reveal-panel"
            : "onuw-client-reveal-panel"
        }
        data-testid={
          isComplete ? "onuw-round-complete-panel" : "onuw-vote-result-panel"
        }
      >
        <div className="onuw-client-panel-heading">
          <span>{isComplete ? "라운드 완료" : "투표 결과 · 수동 공개"}</span>
          <strong>{outcomeLabel}</strong>
        </div>
        <div className="onuw-client-vote-counts">
          {gameState.players.map((player) => {
            const voteCount = voteCountsByPlayerId.get(player.id);
            return (
              <span
                className={voteCount?.isTop ? "is-top" : ""}
                data-testid="onuw-vote-count"
                key={player.id}
              >
                <strong>{player.name}</strong>{" "}
                <em>{voteCount?.count ?? 0}표</em>
              </span>
            );
          })}
        </div>
        <div className="onuw-client-vote-lines">
          {gameState.voteResult.voteLines.map((voteLine) => (
            <span key={`${voteLine.voterPlayerId}-${voteLine.targetPlayerId}`}>
              {playerById.get(voteLine.voterPlayerId)?.name ?? "플레이어"} →{" "}
              {playerById.get(voteLine.targetPlayerId)?.name ?? "플레이어"}
            </span>
          ))}
        </div>
        <p>
          승리 후보 {winningTeams}.{" "}
          {isComplete
            ? "결과가 고정되었습니다."
            : "방장이 필요한 역할 카드를 하나씩 공개합니다."}
        </p>
        {autoVoteNames.length > 0 ? (
          <p data-testid="onuw-auto-vote-result">
            자동 본인 투표: {autoVoteNames.join(", ")}
          </p>
        ) : null}
        {isComplete ? (
          <div className="onuw-client-phase-actions">
            {isHost ? (
              <button
                className="onuw-client-complete-round"
                data-testid="onuw-start-next-round"
                onClick={startNextRound}
                type="button"
              >
                다음 라운드 준비
              </button>
            ) : (
              <span>방장이 다음 진행을 선택합니다.</span>
            )}
            <button onClick={handleLeave} type="button">
              방 나가기
            </button>
          </div>
        ) : isHost ? (
          <div className="onuw-client-reveal-grid">
            {gameState.players.map((player) => {
              const revealedCardId =
                revealedPlayerCardIdByPlayerId.get(player.id) ?? null;
              return (
                <button
                  data-testid="onuw-reveal-player-card"
                  disabled={isRevealedCard(viewerState, revealedCardId)}
                  key={player.id}
                  onClick={() => revealPlayerCard(player.id)}
                  type="button"
                >
                  {player.name} 공개
                </button>
              );
            })}
            {centerSlotIndexes.map((slotIndex) => {
              const revealedCardId =
                revealedCenterCardIdBySlotIndex.get(slotIndex) ?? null;
              return (
                <button
                  data-testid="onuw-reveal-center-card"
                  disabled={isRevealedCard(viewerState, revealedCardId)}
                  key={slotIndex}
                  onClick={() => revealCenterCard(slotIndex)}
                  type="button"
                >
                  중앙 {slotIndex + 1} 공개
                </button>
              );
            })}
            <button
              className="onuw-client-complete-round"
              data-testid="onuw-complete-round"
              onClick={completeRound}
              type="button"
            >
              라운드 완료
            </button>
          </div>
        ) : (
          <p>방장이 카드 공개를 진행 중입니다.</p>
        )}
      </section>
    );
  };

  // Target selection happens directly on the roster tiles / center cards
  // (2026-10-21 roster redesign). The dock keeps only the seer mode switch
  // and the selection hint, at a constant height.
  const renderNightTargetControls = () => {
    if (
      !viewerNightAction ||
      viewerNightAction.status !== "required" ||
      (!isDoppelgangerCopyPhase &&
        !isExplicitTargetRole(
          effectiveNightRoleId ?? viewerNightAction.roleId,
        ) &&
        !canInspectCenterAsWerewolf)
    ) {
      return null;
    }

    return (
      <div
        className="onuw-client-night-targets"
        data-testid="onuw-night-target-panel"
      >
        {effectiveNightRoleId === "seer" ? (
          <div className="onuw-client-night-mode">
            <button
              aria-pressed={seerTargetMode === "center"}
              className={seerTargetMode === "center" ? "is-selected" : ""}
              data-testid="onuw-seer-mode-center"
              disabled={!nightActionAnnouncementReady}
              onClick={() => chooseSeerTargetMode("center")}
              type="button"
            >
              중앙 2장
            </button>
            <button
              aria-pressed={seerTargetMode === "player"}
              className={seerTargetMode === "player" ? "is-selected" : ""}
              data-testid="onuw-seer-mode-player"
              disabled={!nightActionAnnouncementReady}
              onClick={() => chooseSeerTargetMode("player")}
              type="button"
            >
              플레이어 1명
            </button>
          </div>
        ) : null}
        <p>위 보드에서 대상을 직접 탭하세요. {nightSelectionSummary}</p>
      </div>
    );
  };

  const bottomStatus =
    error ??
    (gameState?.phase === "role-peek"
      ? "각자 자신의 역할을 확인합니다. 다른 플레이어의 카드는 비공개로 유지됩니다."
      : "서버에서 동기화된 상태를 확인 중입니다.");

  const handleLeave = () => {
    actions.reset();
    lobbyActions.leaveGame();
    navigateTo("/one-night-ultimate-werewolf/lobby");
  };

  if (!currentRoom) {
    return (
      <main className="onuw-client-shell" data-testid="onuw-client-screen">
        <p>게임 방이 없습니다.</p>
      </main>
    );
  }

  if (!gameState) {
    return (
      <main className="onuw-client-shell" data-testid="onuw-client-screen">
        <section className="onuw-client-empty">
          <strong>플레이어를 기다리는 중</strong>
          <span>원나잇 얼티밋 웨어울프는 3명부터 시작할 수 있습니다.</span>
          <button type="button" onClick={handleLeave}>
            로비로
          </button>
        </section>
      </main>
    );
  }

  return (
    <main
      className="onuw-client-shell"
      data-phase-mode={phaseMode}
      data-testid="onuw-client-screen"
    >
      <header className="onuw-client-top">
        <div>
          <span>
            {phaseModeChips.get(phaseMode) ?? phaseMode} ·{" "}
            {phaseLabels.get(gameState.phase) ?? gameState.phase} ·{" "}
            {connectionStatus}
          </span>
          <strong>원나잇 얼티밋 웨어울프</strong>
        </div>
        <button type="button" onClick={handleLeave}>
          나가기
        </button>
      </header>

      <section className="onuw-client-board-area" aria-label="플레이어 자리">
        {viewerState ? (
          <OneNightRosterBoard
            view={viewerState}
            highlightPlayerId={user?.id ?? null}
            tileInteraction={tileInteraction}
            centerInteraction={centerInteraction}
          />
        ) : null}
        <OneNightEventLog
          entries={logEntries}
          expanded={logExpanded}
          onToggle={() => setLogExpanded((value) => !value)}
        />
      </section>

      <footer className="onuw-client-bottom" data-testid="onuw-client-bottom">
        {gameState.phase === "setup" && isHost ? (
          <section
            className="onuw-client-host-setup"
            data-testid="onuw-client-host-setup"
          >
            <div className="onuw-client-panel-heading">
              <span>방장 준비</span>
              <strong>자리와 역할을 확정합니다</strong>
            </div>

            <div className="onuw-client-seat-list">
              {gameState.seatOrder.map((playerId, index) => {
                const player = playerById.get(playerId);
                const isHostSeat = index === 0;
                return (
                  <div
                    className={
                      isHostSeat
                        ? "is-host-seat onuw-client-seat-chip"
                        : "onuw-client-seat-chip"
                    }
                    data-testid="onuw-seat-chip"
                    key={playerId}
                  >
                    <span>{index + 1}</span>
                    <strong data-testid="onuw-seat-name">
                      {player?.name ?? "플레이어"}
                    </strong>
                    <div>
                      <button
                        aria-label={`${player?.name ?? "플레이어"} 자리 앞으로`}
                        disabled={
                          isHostSeat || index <= 1 || gameState.seatLocked
                        }
                        onClick={() => moveSeat(index, -1)}
                        type="button"
                      >
                        앞
                      </button>
                      <button
                        aria-label={`${player?.name ?? "플레이어"} 자리 뒤로`}
                        data-testid={`onuw-seat-move-back-${index}`}
                        disabled={
                          isHostSeat ||
                          index >= gameState.seatOrder.length - 1 ||
                          gameState.seatLocked
                        }
                        onClick={() => moveSeat(index, 1)}
                        type="button"
                      >
                        뒤
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="onuw-client-role-topline">
              <div>
                <span>역할 선택</span>
                <strong data-testid="onuw-role-count">
                  {draftRoleCount}/{targetRoleCount}
                </strong>
              </div>
              <button
                data-testid="onuw-role-preset-beginner"
                onClick={() =>
                  setDraftRoleIds(makeBeginnerPreset(targetRoleCount))
                }
                type="button"
              >
                초보자 세트
              </button>
            </div>

            <div
              className="onuw-client-duration-card"
              data-testid="onuw-discussion-duration-setting"
            >
              <div>
                <span>토론 시간</span>
                <strong>
                  {formatCountdown(draftDiscussionDurationSeconds)}
                </strong>
              </div>
              <div className="onuw-client-duration-options">
                {discussionDurationOptions.map((seconds) => (
                  <button
                    aria-pressed={draftDiscussionDurationSeconds === seconds}
                    className={
                      draftDiscussionDurationSeconds === seconds
                        ? "is-selected"
                        : ""
                    }
                    data-testid="onuw-discussion-duration-option"
                    key={seconds}
                    onClick={() => setDraftDiscussionDurationSeconds(seconds)}
                    type="button"
                  >
                    {Math.floor(seconds / 60)}분
                  </button>
                ))}
              </div>
            </div>

            <div className="onuw-client-team-summary">
              {(["village", "werewolf", "neutral"] as const).map((team) => (
                <span data-testid={`onuw-team-count-${team}`} key={team}>
                  {teamLabels.get(team)} {countTeam(draftRoleIds, team)}
                </span>
              ))}
            </div>

            <div className="onuw-client-role-grid">
              {roleOrder.map((roleId) => {
                const definition = roleDefinition(roleId);
                const selectedCount = countRole(draftRoleIds, roleId);
                return (
                  <article className="onuw-client-role-row" key={roleId}>
                    <button
                      aria-label={`${definition.nameKo} 빼기`}
                      disabled={selectedCount === 0}
                      onClick={() => removeDraftRole(roleId)}
                      type="button"
                    >
                      -
                    </button>
                    <div>
                      <strong>{definition.nameKo}</strong>
                      <span>
                        {teamLabels.get(definition.team)} · 최대{" "}
                        {definition.maxCopies}
                      </span>
                    </div>
                    <em>{selectedCount}</em>
                    <button
                      aria-label={`${definition.nameKo} 더하기`}
                      disabled={
                        draftRoleCount >= targetRoleCount ||
                        selectedCount >= definition.maxCopies
                      }
                      onClick={() => addDraftRole(roleId)}
                      type="button"
                    >
                      +
                    </button>
                  </article>
                );
              })}
            </div>

            <div className="onuw-client-setup-actions">
              <button
                data-testid="onuw-lock-seat"
                disabled={gameState.seatLocked}
                onClick={lockSeatOrder}
                type="button"
              >
                자리 확정
              </button>
              <button
                data-testid="onuw-role-save"
                disabled={!draftSelectionReady || !rolesChanged}
                onClick={saveRoles}
                type="button"
              >
                역할 저장
              </button>
              <button
                data-testid="onuw-discussion-duration-save"
                disabled={!discussionDurationChanged}
                onClick={saveDiscussionDuration}
                type="button"
              >
                토론 저장
              </button>
              <button
                data-testid="onuw-deal-cards"
                disabled={
                  !gameState.seatLocked ||
                  !persistedSelectionReady ||
                  discussionDurationChanged
                }
                onClick={dealCards}
                type="button"
              >
                카드 배정
              </button>
            </div>
          </section>
        ) : gameState.phase === "setup" ? (
          <section
            className="onuw-client-guest-setup"
            data-testid="onuw-client-guest-setup"
          >
            <strong>방장이 준비를 진행 중입니다</strong>
            <p>
              {gameState.seatLocked
                ? "역할 선택과 카드 배정을 기다립니다."
                : "자리 순서 확정을 기다립니다."}
            </p>
          </section>
        ) : gameState.phase === "role-peek" ? (
          <section
            className="onuw-client-role-peek"
            data-testid="onuw-role-peek-panel"
          >
            <div className="onuw-client-panel-heading">
              <span>역할 확인</span>
              <strong>
                내 역할 ·{" "}
                {myRoleId ? (roleLabels.get(myRoleId) ?? "카드") : "비공개"}
              </strong>
            </div>
            <p>
              {rolePeekConfirmed
                ? "확인 완료. 모든 플레이어가 확인하면 방장이 밤을 시작합니다."
                : "자기 역할을 확인한 뒤 완료를 눌러주세요."}
            </p>
            <div className="onuw-client-flow-summary">
              <span>
                확인 {rolePeekConfirmedCount}/{gameState.players.length}
              </span>
              <span>{allRolePeekConfirmed ? "밤 시작 가능" : "확인 대기"}</span>
            </div>
            <div className="onuw-client-phase-actions">
              <button
                data-testid="onuw-confirm-role-peek"
                disabled={rolePeekConfirmed}
                onClick={confirmRolePeek}
                type="button"
              >
                {rolePeekConfirmed ? "확인 완료됨" : "내 역할 확인 완료"}
              </button>
              {isHost ? (
                <button
                  data-testid="onuw-start-night"
                  disabled={!allRolePeekConfirmed}
                  onClick={startNight}
                  type="button"
                >
                  밤 시작
                </button>
              ) : null}
            </div>
          </section>
        ) : gameState.phase === "night" ? (
          <section
            className="onuw-client-night-panel"
            data-testid="onuw-night-panel"
          >
            <div
              className="onuw-client-night-card"
              data-testid="onuw-night-announcer"
            >
              <span>{pendingAnnouncement ? "현재 선언" : "선언 완료"}</span>
              <strong>
                {pendingAnnouncement
                  ? (roleLabels.get(pendingAnnouncement.roleId) ??
                    pendingAnnouncement.roleId)
                  : "모든 선언 완료"}
              </strong>
              <p>
                {pendingAnnouncement
                  ? nightScript(pendingAnnouncement.roleId)
                  : "남은 아나운서 선언이 없습니다."}
              </p>
              {isHost && pendingAnnouncement ? (
                <button
                  data-testid="onuw-advance-announcement"
                  onClick={advanceAnnouncement}
                  type="button"
                >
                  선언 완료
                </button>
              ) : null}
            </div>

            <div
              className="onuw-client-night-card"
              data-testid="onuw-night-action"
            >
              <span>현재 행동</span>
              <strong>
                {viewerNightAction
                  ? doppelgangerCopiedRoleId
                    ? `도플갱어 → ${roleLabels.get(doppelgangerCopiedRoleId) ?? doppelgangerCopiedRoleId}`
                    : (roleLabels.get(viewerNightAction.roleId) ??
                      viewerNightAction.roleId)
                  : "밤 행동 완료"}
              </strong>
              <p>
                {viewerNightAction?.status === "required"
                  ? nightActionAnnouncementReady
                    ? "내 행동 차례입니다. 앱에서 가능한 행동을 확정합니다."
                    : "아나운서 선언 완료를 기다립니다."
                  : viewerNightAction?.status === "completed"
                    ? "내 행동은 완료되었습니다."
                    : viewerNightAction
                      ? "다른 플레이어의 행동을 기다립니다."
                      : "진행할 밤 행동이 없습니다."}
              </p>
              {renderNightTargetControls()}
              {viewerNightAction?.status === "required" ? (
                <button
                  data-testid="onuw-submit-night-action"
                  disabled={
                    !nightActionAnnouncementReady || !nightActionPayload
                  }
                  onClick={submitCurrentNightAction}
                  type="button"
                >
                  {isDoppelgangerCopyPhase
                    ? "역할 복사"
                    : nightActionButtonLabel(
                        effectiveNightRoleId ?? viewerNightAction.roleId,
                        canInspectCenterAsWerewolf,
                      )}
                </button>
              ) : null}
            </div>
          </section>
        ) : gameState.phase === "day" || gameState.phase === "vote" ? (
          renderVotePanel()
        ) : gameState.phase === "reveal" || gameState.phase === "complete" ? (
          renderRevealPanel()
        ) : (
          <p>{bottomStatus}</p>
        )}
      </footer>
    </main>
  );
}
